import type {
  MealLogConfig,
  MealLogDayData,
  WidgetConfig,
  WidgetWeekEntry,
} from "@/lib/schema";
import { countPhrase, sumOverDays } from "@/widgets/describe";
import { getWidgetModule } from "@/widgets/registry";
import type { DayWidgetModule } from "@/widgets/types";
import { orderedConditions } from "./conditions";
import { DayView } from "./DayView";
import { SettingsEditor } from "./SettingsEditor";
import { maxScoreWeek, scoreDay, scoreWeek } from "./scoring";
import { validate } from "./validate";

function defaults(existing: WidgetConfig[]): MealLogConfig {
  const dayWidgets = existing.filter(
    (w) => getWidgetModule(w.kind)?.scope === "day",
  );
  const maxRow = dayWidgets.reduce((m, w) => Math.max(m, w.row), -1);
  return {
    id: crypto.randomUUID(),
    kind: "meal-log",
    label: "Matdagbok",
    row: maxRow + 1,
    column: 0,
    enabled: true,
    conditions: [],
  };
}

// Meals, not finished days: a finished day with nothing in it loses nothing,
// and an unfinished day full of meals loses the most of all.
function describeWeekData(entry: WidgetWeekEntry): string | null {
  const meals = sumOverDays<MealLogDayData>(
    entry,
    (day) => day?.meals?.length ?? 0,
  );
  return countPhrase(meals, "måltid", "måltider");
}

// Settling the stored order on save is what lets the editor sit still while
// you type (docs/adr/0021); every view sorts on render regardless, since a
// sealed snapshot's conditions never pass through here again.
function normalize(config: MealLogConfig): MealLogConfig {
  return { ...config, conditions: orderedConditions(config.conditions) };
}

export const mealLog: DayWidgetModule<MealLogConfig> = {
  kind: "meal-log",
  label: "Matdagbok",
  scope: "day",
  scoring: "points",
  defaults,
  validate,
  normalize,
  SettingsEditor,
  DayView,
  describeWeekData,
  scoreDay,
  scoreWeek,
  maxScoreWeek,
};
